import React, { useState, useEffect } from 'react';
import {
  Typography,
  Form,
  Input,
  Button,
  Space,
  Tabs,
  Row,
  Col,
  App,
  Switch,
  Modal,
  Table,
  Tag,
  Popconfirm
} from 'antd';
import type { TabsProps } from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ContactsOutlined,
  HolderOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceContentApi } from '../../services/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title } = Typography;
const { TextArea } = Input;

// Sortable Row Component
const SortableRow = ({ children, ...props }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child, index) => {
        if (index === 0) {
          return React.cloneElement(child, {
            children: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  {...listeners}
                  style={{
                    cursor: 'grab',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <HolderOutlined />
                </div>
                {child.props.children}
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

interface Contact {
  id: string;
  type: string;
  companyName: { en: string; zh: string };
  address: { en: string; zh: string };
  phone: string;
  website: string;
  googleMapsLink: string;
  description: { en: string; zh: string };
  isActive: boolean;
  sortOrder: number;
}

const AdminServiceContent: React.FC = () => {
  const [form] = Form.useForm();
  const [contactForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('contact-settings');
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [localContacts, setLocalContacts] = useState<Contact[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const queryClient = useQueryClient();
  const { message: messageApi } = App.useApp();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch service content
  const { data: contentData, isLoading } = useQuery({
    queryKey: ['serviceContent'],
    queryFn: serviceContentApi.getContent
  });

  const content = contentData?.content || null;

  // Initialize local contacts when content is loaded
  useEffect(() => {
    if (content) {
      setLocalContacts(content.contacts || []);
      setHasUnsavedChanges(false);
      // Set form values
      form.setFieldsValue(content);
    }
  }, [content, form]);

  // Watch for form changes
  const handleFormChange = () => {
    setHasUnsavedChanges(true);
  };

  // Update content mutation
  const updateContentMutation = useMutation({
    mutationFn: serviceContentApi.updateContent,
    onSuccess: () => {
      messageApi.success('Service content updated successfully');
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['serviceContent'] });
    },
    onError: (error: any) => {
      messageApi.error(error.response?.data?.message || 'Failed to update service content');
    }
  });


  // Contact mutations (local only - will be saved when Save Settings is pressed)
  const addContactMutation = useMutation({
    mutationFn: async (data: any) => {
      // Generate ID if not provided
      if (!data.id) {
        data.id = `contact-${Date.now()}`;
      }
      
      // Generate sort order if not provided
      const maxOrder = localContacts.length > 0 ? Math.max(...localContacts.map(c => c.sortOrder || 0)) : 0;
      data.sortOrder = maxOrder + 1;
      
      return data; // Return the data instead of making API call
    },
    onSuccess: (newContact) => {
      setLocalContacts(prev => [...prev, newContact]);
      setHasUnsavedChanges(true);
      setContactModalVisible(false);
      contactForm.resetFields();
    },
    onError: () => {
      messageApi.error('Failed to add contact');
    }
  });

  const updateContactMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return { id, data }; // Return the data instead of making API call
    },
    onSuccess: ({ id, data }) => {
      setLocalContacts(prev => prev.map(contact => 
        contact.id === id ? { ...contact, ...data } : contact
      ));
      setHasUnsavedChanges(true);
      setContactModalVisible(false);
      setEditingContact(null);
      contactForm.resetFields();
    },
    onError: () => {
      messageApi.error('Failed to update contact');
    }
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (id: string) => {
      return id; // Return the id instead of making API call
    },
    onSuccess: (id) => {
      setLocalContacts(prev => prev.filter(contact => contact.id !== id));
      setHasUnsavedChanges(true);
    },
    onError: () => {
      messageApi.error('Failed to delete contact');
    }
  });

  const handleSubmit = (values: any) => {
    setLoading(true);
    
    // Clean the contacts data before sending
    const cleanContacts = localContacts.map((contact, index) => {
      const { _id, __v, ...cleanContact } = contact as any;
      return {
        ...cleanContact,
        sortOrder: index + 1
      };
    });

    const updateData = {
      ...values,
      contacts: cleanContacts
    };

    console.log('Sending update data:', updateData);
    console.log('Cleaned contacts:', cleanContacts);

    updateContentMutation.mutate(updateData, {
      onSuccess: () => {
        setHasUnsavedChanges(false);
        messageApi.success('All changes saved successfully');
      },
      onSettled: () => setLoading(false)
    });
  };

  const handleContactSubmit = (values: any) => {
    if (editingContact) {
      // For updates, include the ID in the data
      updateContactMutation.mutate({ id: editingContact.id, data: { ...values, id: editingContact.id } });
    } else {
      // For new contacts, don't include ID - let backend generate it
      addContactMutation.mutate(values);
    }
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    contactForm.setFieldsValue(contact);
    setContactModalVisible(true);
  };

  const handleDeleteContact = (id: string) => {
    deleteContactMutation.mutate(id);
  };

  // Handle drag end for reordering (local only)
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = localContacts.findIndex((contact: Contact) => contact.id === active.id);
      const newIndex = localContacts.findIndex((contact: Contact) => contact.id === over.id);

      if (oldIndex !== undefined && newIndex !== undefined) {
        const newContacts = arrayMove(localContacts, oldIndex, newIndex);
        
        // Update sort order based on new positions
        const updatedContacts = newContacts.map((contact, index) => ({
          ...contact,
          sortOrder: index + 1
        }));

        // Update local state only
        setLocalContacts(updatedContacts);
        setHasUnsavedChanges(true);
      }
    }
  };


  const contactColumns = [
    {
      title: 'Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      render: (sortOrder: number) => (
        <Tag color="purple">{sortOrder}</Tag>
      ),
    },
    {
      title: 'Company Name (EN)',
      dataIndex: ['companyName', 'en'],
      key: 'companyNameEn',
    },
    {
      title: 'Company Name (ZH)',
      dataIndex: ['companyName', 'zh'],
      key: 'companyNameZh',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Contact) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditContact(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this contact?"
            onConfirm={() => handleDeleteContact(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems: TabsProps['items'] = [
    {
      key: 'contact-settings',
      label: (
        <span>
          <HolderOutlined />
          Contact Settings
        </span>
      ),
      children: (
        <div>
          <Title level={4} style={{ marginBottom: 24 }}>Contact Section Titles</Title>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Form.Item 
                label="Section Title (English)" 
                name={['contactSection', 'title', 'en']}
                rules={[{ required: true, message: 'Please enter the English title' }]}
              >
                <Input placeholder="Contact Information" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label="Section Title (Chinese)" 
                name={['contactSection', 'title', 'zh']}
                rules={[{ required: true, message: 'Please enter the Chinese title' }]}
              >
                <Input placeholder="聯絡資訊" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Form.Item 
                label="Section Subtitle (English)" 
                name={['contactSection', 'subtitle', 'en']}
                rules={[{ required: true, message: 'Please enter the English subtitle' }]}
              >
                <Input placeholder="Get in touch with our authorized distributors" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label="Section Subtitle (Chinese)" 
                name={['contactSection', 'subtitle', 'zh']}
                rules={[{ required: true, message: 'Please enter the Chinese subtitle' }]}
              >
                <Input placeholder="與我們的授權經銷商聯繫" />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'contacts',
      label: (
        <span>
          <ContactsOutlined />
          Contacts
        </span>
      ),
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4}>Contact Information</Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingContact(null);
                contactForm.resetFields();
                setContactModalVisible(true);
              }}
            >
              Add Contact
            </Button>
          </div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={localContacts.map(contact => contact.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Table
                      columns={contactColumns}
                      dataSource={localContacts.sort((a, b) => a.sortOrder - b.sortOrder)}
                      rowKey="id"
                      pagination={false}
                      loading={isLoading}
                      components={{
                        body: {
                          row: SortableRow,
                        },
                      }}
                    />
                  </SortableContext>
                </DndContext>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>Service Content Management</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => queryClient.invalidateQueries({ queryKey: ['serviceContent'] })}>
            Refresh
          </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={loading || updateContentMutation.isPending}
                  onClick={() => form.submit()}
                  disabled={!hasUnsavedChanges}
                >
                  {hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
                </Button>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
        />
      </Form>


      {/* Contact Modal */}
      <Modal
        title={editingContact ? 'Edit Contact' : 'Add Contact'}
        open={contactModalVisible}
        onCancel={() => {
          setContactModalVisible(false);
          setEditingContact(null);
          contactForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={contactForm}
          layout="vertical"
          onFinish={handleContactSubmit}
        >
          <Form.Item label="Type" name="type" rules={[{ required: true, message: 'Please enter type' }]}>
            <Input placeholder="e.g., Home Products, Medical Products, Support, General" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Company Name (English)" name={['companyName', 'en']} rules={[{ required: true, message: 'Please enter English company name' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Company Name (Chinese)" name={['companyName', 'zh']} rules={[{ required: true, message: 'Please enter Chinese company name' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Address (English)" name={['address', 'en']} rules={[{ required: true, message: 'Please enter English address' }]}>
                <TextArea rows={3} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Address (Chinese)" name={['address', 'zh']} rules={[{ required: true, message: 'Please enter Chinese address' }]}>
                <TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item label="Phone" name="phone" rules={[{ required: true, message: 'Please enter phone number' }]}>
            <Input />
          </Form.Item>
          
          <Form.Item label="Website" name="website">
            <Input placeholder="https://example.com" />
          </Form.Item>
          
          <Form.Item label="Google Maps Link" name="googleMapsLink">
            <Input placeholder="https://maps.google.com/maps?q=..." />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Description (English)" name={['description', 'en']}>
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Description (Chinese)" name={['description', 'zh']}>
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setContactModalVisible(false);
                setEditingContact(null);
                contactForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={addContactMutation.isPending || updateContactMutation.isPending}
              >
                {editingContact ? 'Update' : 'Add'} Contact
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminServiceContent;
